import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { User } from '../../../@core/services/session-manager.service';
import { UserApiActions } from '../../../@store/user/user.actions';
import {
  selectActiveUsers,
  selectActiveUsersError,
  selectActiveUsersLoading,
  selectActiveUsersTotalCount,
  selectIsAdmin,
} from '../../../@store/user/user.selectors';
import { UserService } from '../../../@store/user/user.service';
import { UserDetails } from '../../../@store/user/user.state.interface';
import { CONFIG } from '../../../config';
import { PaginationComponent } from '../../@shared/pagination/pagination.component';
import { ArchiveUserDialogComponent } from '../archive-user-dialog/archive-user-dialog.component';

@Component({
  selector: 'ciwc-active-users',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatPaginatorModule,
    MatSortModule,
    MatIconModule,
    MatDialogModule,
    MatCardModule,
    MatSelectModule,
    PaginationComponent,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './active-users.component.html',
  styleUrls: ['./active-users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActiveUsersComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly userService = inject(UserService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  readonly isAdmin = this.store.selectSignal(selectIsAdmin);

  readonly activeUsers = this.store.selectSignal(selectActiveUsers);
  readonly activeUsersLength = this.store.selectSignal(selectActiveUsersTotalCount);
  readonly usersLoading = this.store.selectSignal(selectActiveUsersLoading);
  readonly usersError = this.store.selectSignal(selectActiveUsersError);

  protected readonly Math = Math;

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<UserDetails>;

  dataSource: MatTableDataSource<UserDetails> = new MatTableDataSource<UserDetails>();
  displayedColumns: string[] = ['firstName', 'email', 'actions'];
  pageSizeOptions = CONFIG.PAGE_SIZE_OPTIONS;
  pageSize = CONFIG.PAGE_SIZE;
  totalUsers = 0;
  pageIndex = 0;
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;
  currentSort: Sort = { active: 'firstName', direction: 'asc' };

  constructor() {
    effect(() => {
      this.initValues();
    });
  }

  ngOnInit(): void {
    this.loadUsers();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadUsers(): void {
    const payload = {
      status: 'ACTIVE',
      sort: 'firstName',
      offset: 1,
      limit: 10,
    };

    this.store.dispatch(UserApiActions.fetchActiveUsers({ payload }));
  }

  handlePageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;

    const formattedSort =
      this.currentSort.direction === 'desc'
        ? `-${this.currentSort.active}`
        : this.currentSort.active;

    this.store.dispatch(
      UserApiActions.fetchActiveUsers({
        payload: {
          status: 'ACTIVE',
          sort: formattedSort,
          offset: this.pageIndex + 1,
          limit: this.pageSize,
        },
      }),
    );
  }

  sortData(sort: Sort): void {
    this.currentSort = sort;
    const sortField = sort.active === 'firstName' ? 'firstName' : sort.active;
    const formattedSort = sort.direction === 'desc' ? `-${sortField}` : sortField;

    this.store.dispatch(
      UserApiActions.fetchActiveUsers({
        payload: {
          status: 'ACTIVE',
          sort: formattedSort,
          offset: this.pageIndex + 1,
          limit: this.pageSize,
        },
      }),
    );
  }

  goToArchivedUsers(): void {
    this.router.navigate(['/users/archived']);
  }

  async openAddUserDialog(): Promise<void> {
    const dialogRef = this.dialog.open(MatDialog, {
      width: '400px',
      data: { roles: ['USER'] },
    });

    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      this.userService
        .addUser(result)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.loadUsers();
        });
    }
  }

  updatePageSize(size: number): void {
    if (this.paginator) {
      this.paginator.pageSize = size;
      this.pageSize = size;
    }
  }

  editUser(user: User): void {
    this.router.navigate(['/users/edit', user.id]);
  }

  openArchiveDialog(user: User): void {
    const dialogRef = this.dialog.open(ArchiveUserDialogComponent, {
      data: { user },
    });

    dialogRef.afterClosed();
  }

  viewUserDetails(user: User): void {
    this.router.navigate([`/users/${user.id}/view`]);
  }

  private initValues(): void {
    this.dataSource.data = this.activeUsers()?.map((user: UserDetails) => ({
      id: user.id,
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
    }));

    this.totalUsers = this.activeUsersLength();
  }
}
